import { useEffect, useEffectEvent, useState, type KeyboardEvent } from "react";
import { classNames } from "../styles/classNames";

interface ResizeHandleProps {
  /** What it resizes, read by screen readers and shown on hover. */
  readonly label: string;
  /** The size now, between `min` and `max`, in the caller's unit (pixels or percent). */
  readonly value: number;
  readonly min: number;
  readonly max: number;
  /** How far one arrow key moves it. */
  readonly step: number;
  /** The size a pointer at `clientX` asks for, in the same unit. */
  readonly valueAt: (clientX: number) => number;
  readonly onChange: (value: number) => void;
  /** `bar`: a bar between two panes. `edge`: unseen over a border, such as a sidebar's, until hovered, focused or dragged. */
  readonly look: "bar" | "edge";
  /** Placement only. */
  readonly className?: string;
}

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

/**
 * The one way to resize something side to side (F138; P-A): a vertical separator that a pointer
 * drags and the arrow keys, Home and End move (WAI-ARIA APG window splitter). The page's
 * text isn't selected while dragging; the caller owns the size and where it is kept.
 */
export default function ResizeHandle({ label, value, min, max, step, valueAt, onChange, look, className }: ResizeHandleProps) {
  const [dragging, setDragging] = useState(false);
  // The latest bounds and callbacks, so a drag subscribes once, not on every move it causes.
  const moveTo = useEffectEvent((clientX: number) => { onChange(clamp(valueAt(clientX), min, max)); });

  useEffect(() => {
    if (!dragging) return undefined;
    const { cursor, userSelect } = document.body.style;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    const move = (event: PointerEvent) => { moveTo(event.clientX); };
    const stop = () => { setDragging(false); };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", stop);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", stop);
      document.body.style.cursor = cursor;
      document.body.style.userSelect = userSelect;
    };
  }, [dragging]);

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const next = { ArrowLeft: value - step, ArrowRight: value + step, Home: min, End: max }[event.key];
    if (next === undefined) return;
    event.preventDefault();
    onChange(clamp(next, min, max));
  };

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-valuemin={min}
      aria-valuemax={max}
      aria-valuenow={Math.round(value)}
      aria-label={label}
      title={label}
      tabIndex={0}
      onPointerDown={(event) => {
        // No text selection while dragging; that also withholds the focus, so it is given here,
        // and the arrow keys go on where the pointer left off.
        event.preventDefault();
        event.currentTarget.focus();
        // Captured, the pointer's moves reach this page even over a frame in the other pane.
        event.currentTarget.setPointerCapture(event.pointerId);
        setDragging(true);
      }}
      onKeyDown={onKeyDown}
      data-look={look}
      data-dragging={dragging ? "" : undefined}
      className={classNames("app-resize-handle cursor-col-resize select-none focus:outline-none", look === "bar" && "focus:ring-2", className)}
    />
  );
}
